import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ip_back } from "../../config";
import { TE_STATE_FINALIZED } from "../../utils/coachExerciseNavigation";
import { normalizeKotMode } from "../../utils/kotCoachSubmit";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function tieBreakLabel(rule, t) {
  if (rule === "TWO_POINT_DIFFERENCE") {
    return t("kotTieBreakTwoPointDifference");
  }
  return t("kotTieBreakGoldenPoint");
}

/**
 * Compact KoT parameters + live gameplay toggle for the coach live-training view.
 */
export function LiveTrainingKotCoachParams({
  tsId,
  teId,
  exerciseStateId,
  kotSnapshot = null,
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const config = kotSnapshot?.config || {};
  const kotMode = normalizeKotMode(config.match_winner_mode || config.kot_mode);
  const setTarget = Math.max(3, Number(config.set_points_target) || 11);
  const tieBreakRule = config.tie_break_rule || "GOLDEN_POINT";
  const maxRounds = config.max_rounds ?? null;
  const isFinalized = exerciseStateId === TE_STATE_FINALIZED;
  const canEdit = Boolean(tsId && teId) && !isFinalized;

  const infoPills = useMemo(() => {
    const pills = [];
    if (kotMode === "STOP_SCORE_LEAD") {
      pills.push({
        key: "tie",
        label: tieBreakLabel(tieBreakRule, t),
        variant: "rule",
      });
      if (config.stop_deadline_seconds != null) {
        pills.push({
          key: "deadline",
          label: `${config.stop_deadline_seconds}s`,
          variant: "rule",
        });
      }
    }
    if (maxRounds != null && maxRounds !== "") {
      pills.push({
        key: "max",
        label: `${t("kotMaxRounds")} ${maxRounds}`,
        variant: "rule",
      });
    }
    return pills;
  }, [kotMode, tieBreakRule, config.stop_deadline_seconds, maxRounds, t]);

  const saveMode = useCallback(
    async (nextMode) => {
      if (!canEdit || saving || nextMode === kotMode) return;
      setSaving(true);
      setError(null);
      setSavedFlash(false);
      try {
        const resp = await fetch(`${ip_back}api/kot/runtime-config`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            te_id: teId,
            ts_id: tsId,
            kot_mode: nextMode,
          }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || json.status !== "success") {
          throw new Error(json.message || `HTTP ${resp.status}`);
        }
        setSavedFlash(true);
        window.setTimeout(() => setSavedFlash(false), 2000);
      } catch (e) {
        setError(e.message || t("kotRuntimeConfigFailed"));
      } finally {
        setSaving(false);
      }
    },
    [canEdit, saving, kotMode, teId, tsId, t]
  );

  const stopModeLabel =
    t("kotPlayerArenaSetTo", { n: setTarget }) || `First to ${setTarget}`;
  const fullSetLabel = t("kotModeFullSet") || "Full set";

  return (
    <section className="lt-kot-coach-params" aria-label={t("liveTrainingKotGameplay")}>
      <div className="lt-kot-coach-params__head">
        <span className="lt-kot-coach-params__label">
          {t("liveTrainingKotGameplay") || "Gameplay"}
        </span>
        {savedFlash ? (
          <span className="lt-kot-coach-params__saved" role="status">
            {t("liveTrainingKotModeSaved") || "Updated"}
          </span>
        ) : null}
      </div>

      <div
        className={`lt-kot-coach-params__toggle${
          !canEdit ? " lt-kot-coach-params__toggle--disabled" : ""
        }`}
        role="group"
        aria-label={t("kotMatchWinnerMode")}
      >
        <button
          type="button"
          className={`lt-kot-coach-params__mode${
            kotMode === "STOP_SCORE_LEAD" ? " lt-kot-coach-params__mode--active" : ""
          }`}
          disabled={!canEdit || saving}
          aria-pressed={kotMode === "STOP_SCORE_LEAD"}
          onClick={() => saveMode("STOP_SCORE_LEAD")}
        >
          {stopModeLabel}
        </button>
        <button
          type="button"
          className={`lt-kot-coach-params__mode${
            kotMode === "FULL_SET" ? " lt-kot-coach-params__mode--active" : ""
          }`}
          disabled={!canEdit || saving}
          aria-pressed={kotMode === "FULL_SET"}
          onClick={() => saveMode("FULL_SET")}
        >
          {fullSetLabel}
        </button>
      </div>

      {infoPills.length > 0 ? (
        <div className="lt-kot-coach-params__pills" aria-label={t("parameters")}>
          {infoPills.map((pill) => (
            <span
              key={pill.key}
              className={`lt-kot-coach-params__pill lt-kot-coach-params__pill--${pill.variant}`}
            >
              {pill.label}
            </span>
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="lt-kot-coach-params__error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

export default LiveTrainingKotCoachParams;
